import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { pagosProveedorApi } from '../api/pagosProveedor'
import Boton from '../components/Boton'
import EstadoChipsFilter from '../components/EstadoChipsFilter'
import ModalEnviarLegajo from '../components/ModalEnviarLegajo'
import { fmtFecha, fmtFechaHora, fmtMonto } from '../utils/fecha'
import { useStoredState } from '../hooks/useStoredState'
import useDebounce from '../hooks/useDebounce'
import { colors, font, text, space, radius } from '../design/tokens'

// FINANZAS → Pago a proveedores (Martin, 26 oct 2026). El pago se hace en
// Presea; acá se lista (DETALLE_PAGO), se elige y se manda el legajo.

const ESTADOS = [
  { value: 'pendiente', label: 'Pendiente', bg: colors.warnBg, color: colors.warn },
  { value: 'sin_email', label: 'Sin email', bg: colors.errBg, color: colors.err },
  { value: 'enviado', label: 'Enviado', bg: colors.okBg, color: colors.ok },
]
const FILTROS_DEFAULT = { q: '', desde: '', hasta: '', estados: ['pendiente', 'sin_email'] }

export default function PagosProveedoresPage() {
  const navigate = useNavigate()
  const [filtros, setFiltros] = useStoredState('lgr.filtros.pagosProveedor', FILTROS_DEFAULT)
  const [filas, setFilas] = useState([])
  const [meta, setMeta] = useState({ sincronizado_at: null, aviso: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sel, setSel] = useState([])
  const [modal, setModal] = useState(null)
  const [sincronizando, setSincronizando] = useState(false)
  const [lote, setLote] = useState(null)
  const q = useDebounce(filtros.q, 350)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await pagosProveedorApi.listar({
        ...(q ? { q } : {}), ...(filtros.desde ? { desde: filtros.desde } : {}), ...(filtros.hasta ? { hasta: filtros.hasta } : {}),
      })
      setFilas(data.data)
      setMeta({ sincronizado_at: data.sincronizado_at, aviso: data.aviso })
      setError(null)
    } catch {
      setError('No se pudieron cargar los pagos. Reintentá en un momento.')
    } finally { setLoading(false) }
  }, [q, filtros.desde, filtros.hasta])

  useEffect(() => { cargar() }, [cargar])

  const visibles = useMemo(() => filtros.estados.length ? filas.filter(f => filtros.estados.includes(f.estado)) : filas, [filas, filtros.estados])
  const conteo = useMemo(() => Object.fromEntries(ESTADOS.map(e => [e.value, filas.filter(f => f.estado === e.value).length])), [filas])
  const opciones = ESTADOS.map(e => ({ ...e, label: `${e.label} ${conteo[e.value] ?? 0}` }))
  const seleccionables = visibles.filter(f => f.estado === 'pendiente')
  const elegidos = filas.filter(f => sel.includes(f.id))
  const totalSel = elegidos.reduce((a, f) => a + f.total, 0)

  const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])
  const toggleTodos = () => setSel(s => seleccionables.every(f => s.includes(f.id)) ? s.filter(id => !seleccionables.some(f => f.id === id)) : [...new Set([...s, ...seleccionables.map(f => f.id)])])
  const setFiltro = (k) => (e) => setFiltros(f => ({ ...f, [k]: e.target.value }))
  const toggleEstado = (v) => setFiltros(f => ({ ...f, estados: f.estados.includes(v) ? f.estados.filter(x => x !== v) : [...f.estados, v] }))
  const limpiar = () => setFiltros(FILTROS_DEFAULT)

  const actualizar = async () => {
    setSincronizando(true)
    try { await pagosProveedorApi.sincronizar(); await cargar() }
    catch (e) { setError(e.response?.data?.message || 'Presea no respondió.') }
    finally { setSincronizando(false) }
  }

  const enviarLote = async () => {
    setLote({ enviando: true })
    try {
      const { data } = await pagosProveedorApi.enviarLote(sel)
      setLote(data); setSel([]); cargar()
    } catch (e) { setLote({ error: e.response?.data?.message || 'No se pudo enviar el lote.' }) }
  }

  return (
    <div>
      <div style={st.header}>
        <div>
          <h2 style={st.title}>Pago a proveedores</h2>
          <div style={st.sub}>
            Pagos hechos en Presea · {meta.sincronizado_at ? `actualizado ${fmtFechaHora(meta.sincronizado_at)}` : 'sin traer todavía'}
            {meta.aviso && <span style={{ color: colors.warn }}> · {meta.aviso}</span>}
          </div>
        </div>
        <Boton variante="secondary" disabled={sincronizando} onClick={actualizar} title="Trae de Presea los pagos nuevos o modificados">
          {sincronizando ? 'Actualizando…' : '↻ Actualizar desde Presea'}
        </Boton>
      </div>

      <div style={st.filtros}>
        <input id="pp-q" style={{ ...st.fi, minWidth: 260 }} placeholder="Proveedor, código, CUIT, nº de pago o factura" value={filtros.q} onChange={setFiltro('q')} />
        <input id="pp-desde" style={st.fi} type="date" value={filtros.desde} onChange={setFiltro('desde')} aria-label="Desde" />
        <input id="pp-hasta" style={st.fi} type="date" value={filtros.hasta} onChange={setFiltro('hasta')} aria-label="Hasta" />
        <Boton variante="ghost" onClick={limpiar}>Limpiar</Boton>
      </div>
      <EstadoChipsFilter options={opciones} selected={filtros.estados} onToggle={toggleEstado} />

      {lote && !lote.enviando && (
        <div style={lote.error || lote.fallidos?.length ? st.avisoErr : st.avisoOk}>
          {lote.error || <>
            {lote.enviados?.length ?? 0} legajo{lote.enviados?.length === 1 ? '' : 's'} enviado{lote.enviados?.length === 1 ? '' : 's'}.
            {lote.fallidos?.map(f => <div key={f.id}>OP {f.numero}: {f.motivo}</div>)}
          </>}
          <button style={st.cerrarAviso} onClick={() => setLote(null)} aria-label="Cerrar">✕</button>
        </div>
      )}

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>
              <th style={{ ...st.th, width: 32 }}><input type="checkbox" aria-label="Seleccionar todos los pendientes" checked={seleccionables.length > 0 && seleccionables.every(f => sel.includes(f.id))} onChange={toggleTodos} /></th>
              {['Fecha', 'Número', 'Proveedor', 'Importe', 'Estado', 'Acción'].map(h => (
                <th key={h} style={{ ...st.th, ...(h === 'Importe' ? { textAlign: 'right' } : {}) }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && filas.length === 0 ? (
              <tr><td colSpan={7} style={st.empty}>Trayendo los pagos de Presea…</td></tr>
            ) : error ? (
              <tr><td colSpan={7} style={st.empty}>{error}</td></tr>
            ) : visibles.length === 0 ? (
              <tr><td colSpan={7} style={st.empty}>No hay pagos con estos filtros.</td></tr>
            ) : visibles.map(f => {
              const est = ESTADOS.find(e => e.value === f.estado)
              return (
                <tr key={f.id} style={st.tr} onClick={() => navigate(`/finanzas/pagos-proveedores/${f.id}`)}
                  onMouseEnter={e => (e.currentTarget.style.background = colors.bgSoft)} onMouseLeave={e => (e.currentTarget.style.background = '')}
                  title="Ver el pago y su legajo">
                  <td style={st.td} onClick={e => e.stopPropagation()}>
                    {f.estado === 'pendiente' && <input type="checkbox" aria-label={`Seleccionar OP ${f.numero}`} checked={sel.includes(f.id)} onChange={() => toggle(f.id)} />}
                  </td>
                  <td style={st.tdFecha}>{fmtFecha(f.fecha)}</td>
                  <td style={st.tdMono}>{f.numero}</td>
                  <td style={st.td}><div style={st.principal} title={f.proveedor_nombre}>{f.proveedor_nombre}</div></td>
                  <td style={st.tdImporte}>$ {fmtMonto(f.total)}</td>
                  <td style={st.td}><span style={{ ...st.badge, background: est.bg, color: est.color }}>{est.label}</span></td>
                  <td style={st.td} onClick={e => e.stopPropagation()}>
                    {f.estado === 'enviado'
                      ? <span style={st.chipGris} title={`${f.ultimo_envio?.usuario ?? ''} · ${f.ultimo_envio?.canal ?? ''}`}>⏳ {fmtFechaHora(f.ultimo_envio?.enviado_at)}</span>
                      : <Boton variante="send" sm onClick={() => setModal(f.id)}>→ Enviar</Boton>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {sel.length > 0 && (
        <div style={st.barra}>
          <span>{sel.length} pago{sel.length === 1 ? '' : 's'} · <span style={st.mono}>$ {fmtMonto(totalSel)}</span> · cada legajo sale por mail de LGR al email del proveedor</span>
          <span style={{ display: 'flex', gap: space.md, alignItems: 'center' }}>
            <Boton variante="primary" disabled={lote?.enviando} onClick={enviarLote}>{lote?.enviando ? 'Enviando…' : `✉ Enviar ${sel.length} legajo${sel.length === 1 ? '' : 's'}`}</Boton>
          </span>
        </div>
      )}

      {modal && (
        <ModalEnviarLegajo pagoId={modal} onClose={() => setModal(null)}
          onEnviado={(_, { cerrar }) => { cargar(); if (cerrar) setModal(null) }} />
      )}
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { fontSize: text.sec, color: colors.textSec, marginTop: 2 },
  filtros: { display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center', marginBottom: space.md, background: colors.bg, padding: space.lg, borderRadius: radius.md, border: `1px solid ${colors.border}` },
  fi: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', minWidth: 140, background: colors.bg },
  tableWrap: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums', marginTop: space.md },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: '8px 8px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 48, cursor: 'pointer' },
  td: { padding: '6px 8px', verticalAlign: 'middle', color: colors.text },
  tdSec: { padding: '6px 8px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSub, whiteSpace: 'nowrap' },
  tdFecha: { padding: '6px 8px', verticalAlign: 'middle', fontFamily: font.mono, color: colors.textSec, whiteSpace: 'nowrap' },
  tdMono: { padding: '6px 8px', verticalAlign: 'middle', fontFamily: font.mono, whiteSpace: 'nowrap' },
  tdImporte: { padding: '6px 8px', verticalAlign: 'middle', fontFamily: font.mono, fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'right' },
  principal: { fontWeight: 500, maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mono: { fontFamily: font.mono },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' },
  chipGris: { padding: '2px 8px', background: colors.borderSoft, border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.meta, color: colors.textSec, whiteSpace: 'nowrap', fontFamily: font.mono },
  empty: { padding: 32, textAlign: 'center', color: colors.textMeta },
  barra: { position: 'sticky', bottom: 0, marginTop: space.md, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, flexWrap: 'wrap', padding: '10px 14px', background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: '0 -2px 8px rgba(15,23,42,0.08)', fontSize: text.body },
  avisoOk: { position: 'relative', padding: '8px 32px 8px 12px', marginTop: space.md, background: colors.okSoft, border: `1px solid ${colors.okBg}`, borderRadius: radius.sm, fontSize: text.sec },
  avisoErr: { position: 'relative', padding: '8px 32px 8px 12px', marginTop: space.md, background: colors.errBg, color: colors.err, borderRadius: radius.sm, fontSize: text.sec },
  cerrarAviso: { position: 'absolute', top: 6, right: 8, border: 'none', background: 'none', cursor: 'pointer', color: colors.textSec },
}
